/**
 * @fileoverview Point d'entrée principal (Orchestrateur) de l'application.
 * Ce fichier ne contient pas de logique métier complexe ni de manipulation directe du DOM.
 * Son rôle est de lier les événements utilisateurs (clics, saisies) aux actions appropriées
 * (base de données, traitement d'image, mises à jour de l'interface).
 */

import { CONFIG } from './config.js';
import { openDatabase, addComic, getAllComics, deleteComic, updateComic, findComicByIsbn, setLoan, clearLoan, getAllWishItems, deleteWishItem, addWishItem, findWishItemByIsbn, clearAllComics, clearAllWishItems } from './db.js';
import { resizeImage, resizeBase64Image, fetchImageAsBase64 } from './image.js';
import { initScanner, bindImageScan } from './scanner.js';
import { fetchBookData } from './api-books.js';
import { renderCards, renderStats, initSidebar, initDropdown, showNotification, showLoader, hideLoader } from './ui.js';
import { cleanText, debugLog } from './utils.js';
import { openLoanDialog } from './loan.js';
import * as viewState from './view-state.js';
import { initToolbar, updateCount } from './toolbar.js';
import { initNavigation, updateAllBadge, updateLoanBadge, updateWishBadge } from './navigation.js';
import { initRouter } from './router.js';
import { initWishlist, renderWishlist, openAddWishDialog } from './wishlist.js';
import { openDialog, closeDialog } from './dialogs.js';

// ==========================================
// --- ÉTAT GLOBAL DE L'APPLICATION ---
// ==========================================

/** * Identifiant de la bande dessinée en cours d'édition.
 * S'il vaut `null`, le formulaire est en mode "Création".
 * @type {number|null}
 */
let currentEditingId = null;

/**
 * Stocke temporairement l'image de couverture récupérée par l'API OpenLibrary
 * (via le scanner) en attendant que l'utilisateur valide le formulaire.
 * @type {string|null}
 */
let scannedCoverBase64 = null;

/**
 * Identifiant du souhait en attente de transfert vers la collection.
 * Renseigné par `_onMoveToCollection`, consommé et remis à null après la sauvegarde.
 * @type {number|null}
 */
let pendingWishId = null;

/**
 * Données d'import en attente de traitement (mode additif ou remplacement).
 * Défini lors de la sélection du fichier, consommé après validation du mode.
 * @type {Object|null}
 */
let pendingImportData = null;

// ==========================================
// --- INITIALISATION (DÉMARRAGE) ---
// ==========================================

/**
 * Point d'entrée asynchrone exécuté lorsque le DOM est entièrement chargé.
 * Initialise la base de données, les composants de l'interface, et affiche la liste.
 */
document.addEventListener('DOMContentLoaded', async () => {
    try {
        // 1. Initialisation de la base locale
        await openDatabase();

        // 2. Initialisation des composants UI indépendants
        initSidebar();
        initDropdown();
        initNavigation();
        initToolbar();

        // 2bis. Routing par hash — appelé après les modules abonnés à viewState
        // pour que toute mise à jour initiale du filtre soit reflétée dans l'UI.
        initRouter();

        // 3. Initialisation du scanner : caméra + scan depuis un fichier image
        const _onBookFound = (bookData) => {
            _fillForm(bookData);
            scannedCoverBase64 = bookData.coverBase64;
        };
        initScanner(_onBookFound);
        bindImageScan('input-isbn-image', _onBookFound);

        // 3bis. Initialisation du module wishlist
        initWishlist({
            onDataChanged: () => _renderView(),
            onMoveToCollection: (item) => _onMoveToCollection(item),
        });

        // 4. Branchement des événements sur le reste de l'interface
        _bindForm();
        _bindAddComic();
        _bindAddWish();
        _bindSearch();
        _bindExportImport();
        _bindOptimize();

        // 5. Abonnement au viewState pour rendre la vue à chaque changement
        viewState.subscribe(() => _renderView());

        // 6. Premier affichage de la collection
        await _renderView();
    } catch (error) {
        console.error("Erreur critique lors de l'initialisation de l'application :", error);
    }
});

// ==========================================
// --- CYCLE DE VIE ET AFFICHAGE ---
// ==========================================

/**
 * Construit l'objet de callbacks passé à `renderCards()`.
 * @private
 * @returns {{ onEdit: Function, onDelete: Function, onToggleLoan: Function }}
 */
function _buildCardCallbacks() {
    return {
        onEdit: (comic) => _startEditing(comic),
        onDuplicate: (comic) => _duplicateComic(comic),
        onDelete: async (comic) => {
            if (confirm(`Voulez-vous vraiment supprimer "${comic.title}" ?`)) {
                await deleteComic(comic.id);
                await _renderView();
            }
        },
        onToggleLoan: async (comic) => {
            // BD déjà prêtée → demander confirmation du retour
            if (comic.loan && comic.loan.borrower) {
                if (confirm(`Marquer "${comic.title}" comme rendue par ${comic.loan.borrower} ?`)) {
                    await clearLoan(comic.id);
                    await _renderView();
                }
                return;
            }

            // BD disponible → ouvrir le dialog de saisie
            const result = await openLoanDialog();
            if (!result) return; // L'utilisateur a annulé

            await setLoan(comic.id, result);
            await _renderView();
        },
    };
}

/**
 * Fonction maîtresse de rendu : charge la collection, applique le filtre de vue
 * (`viewState.filter`), le filtre textuel de recherche, le tri (`viewState.sort`),
 * puis pousse vers l'UI. Met à jour le compteur et les stats globales.
 *
 * Cette fonction est appelée :
 *  - au démarrage (premier rendu),
 *  - à chaque changement de `viewState` (via subscribe),
 *  - après chaque mutation (ajout, suppression, prêt, retour).
 *
 * @private
 * @returns {Promise<void>}
 */
async function _renderView() {
    const state = viewState.get();
    const isWishlist = state.section === 'wishlist';

    // Visibilité des zones principales selon la section active
    document.getElementById('comic-list')?.classList.toggle('is-hidden', isWishlist);
    document.getElementById('wish-list')?.classList.toggle('is-hidden', !isWishlist);
    document.querySelector('.toolbar')?.classList.toggle('is-hidden', isWishlist);

    // Fetch parallèle — les deux stores sont toujours nécessaires pour les indicateurs globaux
    const [allComics, allWishItems] = await Promise.all([getAllComics(), getAllWishItems()]);

    // Indicateurs globaux — toujours affichés quelle que soit la section active
    renderStats(allComics);
    updateAllBadge(allComics.length);
    updateLoanBadge(allComics.filter(c => c.loan && c.loan.borrower).length);
    updateWishBadge(allWishItems.length);

    if (isWishlist) {
        // Tri par date d'ajout décroissante (les plus récents en haut)
        renderWishlist([...allWishItems].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0)));
        return;
    }

    // --- Section collection ---
    const filterText = cleanText(document.getElementById('input-search')?.value ?? null);

    const filtered = _applyFilters(allComics, state.filter, filterText);
    const sorted = _applySort(filtered, state.sort);

    renderCards(sorted, _buildCardCallbacks(), state.layout);
    updateCount(sorted.length);
}

/**
 * Applique successivement le filtre de vue et le filtre textuel.
 *
 * @private
 * @param {Object[]} comics
 * @param {string} viewFilter - 'all' | 'loaned'
 * @param {string} textFilter - Texte de recherche normalisé (peut être vide).
 * @returns {Object[]}
 */
function _applyFilters(comics, viewFilter, textFilter) {
    let result = comics;

    if (viewFilter === 'loaned') {
        result = result.filter(c => c.loan && c.loan.borrower);
    }

    if (textFilter) {
        result = result.filter(c => _matchesFilter(c, textFilter));
    }

    return result;
}

/**
 * Applique le tri selon la clé fournie par `viewState.sort`.
 * Les BD sans valeur pour la clé de tri sont rejetées en fin de liste.
 *
 * @private
 * @param {Object[]} comics
 * @param {string} sortKey - 'recent' | 'series' | 'title' | 'loanDate'
 * @returns {Object[]} Nouveau tableau trié (n'altère pas l'entrée).
 */
function _applySort(comics, sortKey) {
    const copy = [...comics];

    switch (sortKey) {
        case 'series':
            // Tri par série puis numéro de tome
            return copy.sort((a, b) => {
                const textA = (a.series || a.title || '').trim();
                const textB = (b.series || b.title || '').trim();
                const cmp = textA.localeCompare(textB, 'fr', { sensitivity: 'base' });
                if (cmp !== 0) return cmp;
                return (Number(a.volume) || 0) - (Number(b.volume) || 0);
            });

        case 'title':
            return copy.sort((a, b) =>
                (a.title || '').localeCompare(b.title || '', 'fr', { sensitivity: 'base' })
            );

        case 'loanDate':
            // Prêts les plus récents en haut ; BD non prêtées rejetées en fin
            return copy.sort((a, b) => {
                const dateA = a.loan?.date ?? -Infinity;
                const dateB = b.loan?.date ?? -Infinity;
                return dateB - dateA;
            });

        case 'recent':
        default:
            // Ajouts les plus récents en haut
            return copy.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
    }
}

// ==========================================
// --- GESTION DU FORMULAIRE ---
// ==========================================

/**
 * Pré-remplit le formulaire avec les champs partagés d'une BD existante pour créer
 * un nouveau tome : série, auteurs, éditeur, tags sont copiés ; le numéro de tome
 * est incrémenté de 1 ; titre, ISBN et couverture sont laissés vides.
 * @private
 * @param {Object} comic - La BD source à dupliquer.
 */
function _duplicateComic(comic) {
    _resetForm();

    const nextVolume = comic.volume != null && !isNaN(Number(comic.volume))
        ? Number(comic.volume) + 1
        : null;

    _fillForm({
        series:    comic.series,
        volume:    nextVolume,
        authors:   Array.isArray(comic.authors) ? comic.authors.join(', ') : comic.authors,
        publisher: comic.publisher,
        tags:      Array.isArray(comic.tags) ? comic.tags.join(', ') : comic.tags,
    });

    const sectionTitle = document.getElementById('form-title');
    if (sectionTitle) sectionTitle.textContent = 'Nouveau tome';

    _openAddComicDialog();
}

/**
 * Branche le bouton « + Ajouter une BD » de la sidebar sur l'ouverture du dialog.
 * @private
 */
function _bindAddComic() {
    document.getElementById('btn-add-comic')?.addEventListener('click', () => {
        _resetForm();
        _openAddComicDialog();
    });
}

/**
 * Branche le bouton « + Ajouter un souhait » de la sidebar sur le dialog wishlist.
 * @private
 */
function _bindAddWish() {
    document.getElementById('btn-add-wish')?.addEventListener('click', () => {
        openAddWishDialog();
    });
}

/**
 * Callback passé au module wishlist : pré-remplit le formulaire collection avec les
 * données du souhait, ouvre le dialog, puis tente de récupérer la couverture
 * automatiquement via l'ISBN si disponible.
 * @private
 * @param {Object} item - L'item wishlist à transférer.
 */
async function _onMoveToCollection(item) {
    // Réinitialise l'état du formulaire (scannedCoverBase64, currentEditingId, etc.)
    _resetForm();

    // pendingWishId doit être réaffecté après _resetForm qui le remet à null
    pendingWishId = item.id;

    _fillForm({
        title:     item.title,
        series:    item.series,
        volume:    item.volume,
        authors:   Array.isArray(item.authors) ? item.authors.join(', ') : (item.authors ?? ''),
        publisher: item.publisher,
        isbn:      item.isbn,
    });

    const sectionTitle = document.getElementById('form-title');
    if (sectionTitle) sectionTitle.textContent = 'Ajouter à la collection';

    _openAddComicDialog();

    // Récupération automatique de la couverture si un ISBN est disponible
    if (!item.isbn) return;

    showLoader('Récupération de la couverture…');
    try {
        const result = await fetchBookData(item.isbn);
        if (result?.coverUrl) {
            scannedCoverBase64 = await fetchImageAsBase64(result.coverUrl);
            if (scannedCoverBase64) {
                showNotification('Couverture récupérée automatiquement.', 'success');
            } else {
                showNotification('Informations trouvées, mais sans couverture.', 'warning');
            }
        }
    } catch (err) {
        console.error('Erreur lors de la récupération de la couverture :', err);
    } finally {
        hideLoader();
    }
}

/**
 * Ouvre le dialog d'ajout/édition en mode modal et focalise le premier champ.
 * @private
 */
function _openAddComicDialog() {
    openDialog('add-comic-dialog');
    document.getElementById('input-title')?.focus();
}

/**
 * Ferme le dialog d'ajout/édition s'il est ouvert.
 * @private
 */
function _closeAddComicDialog() {
    closeDialog('add-comic-dialog');
}

/**
 * Attache les écouteurs d'événements pour la soumission et l'annulation du formulaire.
 * @private
 */
function _bindForm() {
    const form = document.getElementById('add-comic-form');
    const btnCancel = document.getElementById('btn-cancel');
    const dialog = document.getElementById('add-comic-dialog');

    // Annulation explicite (bouton) ou implicite (Échap géré par <dialog>)
    btnCancel.addEventListener('click', () => {
        _resetForm();
        _closeAddComicDialog();
    });
    dialog.addEventListener('cancel', () => _resetForm());

    // Validation et sauvegarde (Création ou Mise à jour)
    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const coverInput = document.getElementById('input-cover');
        let coverDataUrl = null;

        // Priorité 1 : Fichier image uploadé manuellement
        if (coverInput.files?.[0]) {
            coverDataUrl = await resizeImage(coverInput.files[0], CONFIG.MAX_IMAGE_WIDTH);
        }
        // Priorité 2 : Image récupérée via le scanner
        else if (scannedCoverBase64) {
            coverDataUrl = scannedCoverBase64;
        }
        // Pas d'image → cover: null ; un placeholder gris s'affichera en mode vignettes

        // Construction de l'objet BD standardisé
        const comicData = {
            title: document.getElementById('input-title').value,
            series: document.getElementById('input-series').value,
            volume: parseInt(document.getElementById('input-volume').value, 10) || null,
            authors: document.getElementById('input-authors').value.split(',').map(a => a.trim()),
            tags: document.getElementById('input-tags').value.split(',').map(t => t.trim()),
            publisher: document.getElementById('input-publisher').value.trim(),
            isbn: document.getElementById('input-isbn').value.trim(),
            addedat: document.getElementById('input-addedat').value.trim(),
            cover: coverDataUrl,
        };

        try {
            if (currentEditingId) {
                // Mode Édition
                await updateComic({ ...comicData, id: currentEditingId });
            } else {
                if (comicData.isbn) {
                    const existing = await findComicByIsbn(comicData.isbn);

                    if (existing) {
                        const title = existing.series
                            ? `${existing.series}${existing.volume ? ` - Tome ${existing.volume}` : ''}`
                            : existing.title;

                        showNotification(
                            `Impossible de sauvegarder : "${title}" est déjà dans ta collection.`,
                            'error'
                        );
                        return;
                    }
                }

                // Mode Création
                await addComic(comicData);

                // Si la BD vient d'un souhait, supprimer ce dernier
                if (pendingWishId !== null) {
                    await deleteWishItem(pendingWishId);
                    pendingWishId = null;
                }
            }
            _resetForm(); // Vide le formulaire après succès
            _closeAddComicDialog();
            await _renderView();
        } catch (error) {
            console.error("Erreur lors de la sauvegarde :", error);
            alert("Impossible de sauvegarder la BD.");
        }
    });
}

/**
 * Prépare le dialog d'édition avec les données d'une BD existante.
 * @private
 * @param {Object} comic - L'objet BD sélectionné par l'utilisateur.
 */
function _startEditing(comic) {
    // 1. Mise à jour de l'état global
    currentEditingId = comic.id;
    scannedCoverBase64 = comic.cover; // On garde la couverture existante par défaut

    // 2. Pré-remplissage du formulaire
    _fillForm({
        title: comic.title,
        series: comic.series,
        volume: comic.volume,
        authors: Array.isArray(comic.authors) ? comic.authors.join(', ') : comic.authors,
        publisher: comic.publisher,
        tags: Array.isArray(comic.tags) ? comic.tags.join(', ') : comic.tags,
        isbn: comic.isbn,
        addedAt: comic.addedAt
    });

    // 3. Bascule du titre en mode édition
    const sectionTitle = document.getElementById('form-title');
    if (sectionTitle) sectionTitle.textContent = "Modifier la BD";

    // 4. Ouverture du dialog (focus initial sur le premier champ géré dedans)
    _openAddComicDialog();
}

/**
 * Vide le formulaire, réinitialise l'état d'édition et restaure les libellés d'origine.
 * Efface également pendingWishId pour annuler tout transfert wishlist en cours.
 * @private
 */
function _resetForm() {
    document.getElementById('add-comic-form').reset();
    scannedCoverBase64 = null;
    currentEditingId = null;
    pendingWishId = null;

    const sectionTitle = document.getElementById('form-title');
    if (sectionTitle) sectionTitle.textContent = "Nouvelle BD";
}

/**
 * Fonction utilitaire interne pour assigner rapidement des valeurs aux champs d'un formulaire.
 * * @private
 * @param {Object} data - Dictionnaire clé/valeur des données à insérer.
 */
function _fillForm(data) {
    const set = (id, val) => {
        const el = document.getElementById(id);
        if (el) el.value = val ?? '';
    };

    set('input-title', data.title);
    set('input-series', data.series);
    set('input-volume', data.volume);
    set('input-authors', data.authors);
    set('input-publisher', data.publisher);
    set('input-tags', data.tags);
    set('input-isbn', data.isbn);
    set('input-addedat', data.addedAt);
}

// ==========================================
// --- RECHERCHE ET FILTRES ---
// ==========================================

/**
 * Configure les événements liés à la barre de recherche textuelle.
 * * @private
 */
function _bindSearch() {
    const inputSearch = document.getElementById('input-search');
    const searchForm = document.getElementById('search-form');
    let timeout; // Minuteur pour le Debounce (anti-rebond)

    // Recherche "en direct" au clavier avec un délai de 500ms
    inputSearch?.addEventListener('input', (e) => {
        const filterText = cleanText(inputSearch.value ?? null);
        const inputSearchMinChar = inputSearch.minlength ?? 3;

        if ((filterText?.length ?? 0) >= inputSearchMinChar) {
            clearTimeout(timeout);
            timeout = setTimeout(_renderView, 500);
        }
    });

    // Sécurité : empêche le rechargement de la page si on tape "Entrée"
    searchForm?.addEventListener('submit', (e) => {
        e.preventDefault();
        _renderView();
    });
}

/**
 * Évalue si une BD correspond à une chaîne de caractères tapée par l'utilisateur.
 * Recherche dans le titre, série, éditeur, isbn, auteurs et tags.
 * * @private
 * @param {Object} comic - La BD à tester.
 * @param {string} filterText - Le texte de recherche (normalisé en minuscules et sans accents).
 * @returns {boolean} `true` si la BD doit être affichée, `false` sinon.
 */
function _matchesFilter(comic, filterText) {
    if (!filterText) return true;

    // Helper pour comparer le texte nettoyé
    const matches = (val) => cleanText(val).includes(filterText);

    // Helper pour chercher dans un tableau de textes (auteurs, tags)
    const matchesArray = (arr) => Array.isArray(arr)
        ? arr.some(v => matches(v))
        : matches(arr);

    return matches(comic.title) ||
        matches(comic.series) ||
        matches(comic.publisher) ||
        matches(comic.isbn) ||
        matchesArray(comic.authors) ||
        matchesArray(comic.tags);
}

// ==========================================
// --- FONCTIONNALITÉS ANNEXES ---
// ==========================================

/**
 * Génère un nom de fichier export horodaté + domaine.
 * Format : YYYYMMDD-BDCOLLECTION-{hostname}.json
 * Pour les URLs locales (localhost, 127.*, 192.*, etc.), utilise « local ».
 * @private
 * @returns {string} Nom de fichier (ex. 20260601-BDCOLLECTION-bobo_com.json)
 */
function _getExportFilename() {
    const now = new Date();
    const date = now.toISOString().split('T')[0].replace(/-/g, '');

    let hostname = window.location.hostname.toLowerCase();

    // Détecte les URLs locales
    const isLocal = hostname === 'localhost' || hostname === '::1' || /^(127\.|192\.|10\.|172\.)/.test(hostname);
    if (isLocal) {
        hostname = 'local';
    } else {
        hostname = hostname.replace(/\./g, '_');
    }

    return `${date}-BDCOLLECTION-${hostname}.json`;
}

/**
 * Branche les boutons d'exportation et d'importation.
 *
 * Format export : `{ comics: [...], wishlist: [...] }` (JSON indenté).
 * Rétrocompatibilité import : un tableau nu `[...]` est traité comme la collection seule.
 * Import : affiche un dialogue de choix entre mode additif et remplacement.
 * @private
 */
function _bindExportImport() {
    // --- Export ---
    document.getElementById('btn-export').addEventListener('click', async () => {
        try {
            const [comics, wishlist] = await Promise.all([getAllComics(), getAllWishItems()]);
            const payload = { comics, wishlist };

            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const fileUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = _getExportFilename();
            link.click();

            URL.revokeObjectURL(fileUrl);
        } catch (err) {
            console.error("Erreur lors de l'export :", err);
            showNotification("Impossible d'exporter les données.", 'error');
        }
    });

    // --- Import ---
    document.getElementById('btn-import').addEventListener('click', () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.json';

        fileInput.addEventListener('change', (event) => {
            const file = event.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const parsed = JSON.parse(e.target.result);

                    // Détection du format : tableau nu (ancien) ou objet unifié (nouveau)
                    let comicsToImport = [];
                    let wishlistToImport = [];

                    if (Array.isArray(parsed)) {
                        // Ancien format : tableau de BD uniquement
                        comicsToImport = parsed;
                    } else if (parsed && typeof parsed === 'object') {
                        if (Array.isArray(parsed.comics))   comicsToImport   = parsed.comics;
                        if (Array.isArray(parsed.wishlist)) wishlistToImport = parsed.wishlist;
                    } else {
                        throw new Error('Format non reconnu.');
                    }

                    // Mémoriser les données et afficher le dialogue de choix
                    pendingImportData = { comicsToImport, wishlistToImport };
                    openDialog('import-mode-dialog');
                } catch (err) {
                    console.error("Erreur lors de l'import :", err);
                    showNotification("Le fichier sélectionné n'est pas un JSON valide.", 'error');
                }
            };
            reader.readAsText(file);
        });

        fileInput.click();
    });

    // --- Dialog de choix du mode d'import ---
    document.getElementById('btn-import-additive').addEventListener('click', () => {
        closeDialog('import-mode-dialog');
        _importAdditive();
    });

    document.getElementById('btn-import-replace').addEventListener('click', () => {
        closeDialog('import-mode-dialog');
        _importReplacement();
    });

    document.getElementById('btn-import-cancel').addEventListener('click', () => {
        closeDialog('import-mode-dialog');
        pendingImportData = null;
    });
}

/**
 * Importe les données en mode additif : ajoute les BD/souhaits à la collection existante,
 * en ignorant les doublons (détectés par ISBN).
 * @private
 */
async function _importAdditive() {
    if (!pendingImportData) return;

    const { comicsToImport, wishlistToImport } = pendingImportData;

    try {
        // Import collection — déduplication ISBN
        let comicsAdded = 0;
        for (const comic of comicsToImport) {
            if (comic.isbn && await findComicByIsbn(comic.isbn)) continue;
            const { id, ...data } = comic;
            await addComic(data);
            comicsAdded++;
        }

        // Import wishlist — déduplication ISBN
        let wishAdded = 0;
        for (const item of wishlistToImport) {
            if (item.isbn && await findWishItemByIsbn(item.isbn)) continue;
            const { id, ...data } = item;
            await addWishItem(data);
            wishAdded++;
        }

        await _renderView();

        // Feedback
        const parts = [];
        if (comicsAdded > 0) parts.push(`${comicsAdded} BD`);
        if (wishAdded > 0)   parts.push(`${wishAdded} souhait${wishAdded > 1 ? 's' : ''}`);

        if (parts.length > 0) {
            showNotification(`${parts.join(' et ')} importé${comicsAdded + wishAdded > 1 ? 's' : ''} avec succès !`, 'success');
        } else {
            showNotification('Aucune nouvelle entrée à importer (déjà présents ou fichier vide).', 'warning');
        }
    } catch (err) {
        console.error("Erreur lors de l'import additif :", err);
        showNotification("Erreur lors de l'import.", 'error');
    } finally {
        pendingImportData = null;
    }
}

/**
 * Importe les données en mode remplacement : vide la collection et les souhaits,
 * puis importe les données du fichier. Demande une double confirmation.
 * @private
 */
async function _importReplacement() {
    if (!pendingImportData) return;

    // Confirmation finale (irréversible)
    const confirmed = confirm(
        '⚠️  ATTENTION : Cette action va SUPPRIMER définitivement votre collection et vos souhaits, ' +
        'puis les remplacer par les données du fichier.\n\n' +
        'Cette action est IRRÉVERSIBLE.\n\n' +
        'Êtes-vous certain de vouloir continuer ?'
    );
    if (!confirmed) {
        pendingImportData = null;
        return;
    }

    const { comicsToImport, wishlistToImport } = pendingImportData;

    try {
        showLoader();

        // Vider les deux stores
        await Promise.all([clearAllComics(), clearAllWishItems()]);

        // Importer les données sans déduplication (la base est vide)
        let comicsAdded = 0;
        for (const comic of comicsToImport) {
            const { id, ...data } = comic;
            await addComic(data);
            comicsAdded++;
        }

        let wishAdded = 0;
        for (const item of wishlistToImport) {
            const { id, ...data } = item;
            await addWishItem(data);
            wishAdded++;
        }

        hideLoader();
        await _renderView();

        // Feedback
        const parts = [];
        if (comicsAdded > 0) parts.push(`${comicsAdded} BD`);
        if (wishAdded > 0)   parts.push(`${wishAdded} souhait${wishAdded > 1 ? 's' : ''}`);

        const message = parts.length > 0
            ? `Collection remplacée : ${parts.join(' et ')} importé${comicsAdded + wishAdded > 1 ? 's' : ''}.`
            : 'Collection remplacée (fichier vide).';
        showNotification(message, 'success');
    } catch (err) {
        console.error("Erreur lors de l'import remplacement :", err);
        hideLoader();
        showNotification("Erreur lors de l'import.", 'error');
    } finally {
        pendingImportData = null;
    }
}

/**
 * Convertit de force toutes les anciennes images de la base de données (PNG/JPG) en WebP à 243px.
 * Utile pour migrer un ancien format de données vers la nouvelle architecture optimisée.
 * * @private
 */
function _bindOptimize() {
    document.getElementById('btn-optimize').addEventListener('click', async () => {
        if (!confirm("Optimiser toutes les images en WebP (243px) ?")) return;

        try {
            const comics = await getAllComics();
            let count = 0;

            for (const comic of comics) {
                // Si l'image existe et n'est pas déjà un WebP
                if (comic.cover && !comic.cover.startsWith('data:image/webp')) {
                    comic.cover = await resizeBase64Image(comic.cover, CONFIG.MAX_IMAGE_WIDTH);
                    await updateComic(comic);
                    count++;
                }
            }

            alert(count > 0
                ? `✅ ${count} image(s) optimisée(s).`
                : "Toutes vos images sont déjà en WebP optimisé !"
            );

            if (count > 0) await _renderView();
        } catch (error) {
            console.error("Erreur lors de l'optimisation :", error);
            alert("Une erreur est survenue pendant l'optimisation.");
        }
    });
}
